import html2canvas from "html2canvas";
import type { Options as Html2CanvasOptions } from "html2canvas";

type ImageFormat = "png" | "jpeg" | "webp";

export interface ExportOptions {
  format?: ImageFormat;
  quality?: number;
  canvasOptions?: Partial<Html2CanvasOptions>;
}

interface OnCloneTransform {
  selector: string;
  styles: Partial<CSSStyleDeclaration>;
}

interface ImageExportOptions extends ExportOptions {
  transforms?: OnCloneTransform[];
}

class ImageHelpers {
  /**
   * Generic download trigger — accepts any data URL or object URL string.
   * Shared by both direct callers and useFileDownloader consumers.
   */
  static downloadImage = (blob: string, fileName: string): void => {
    const a = document.createElement("a");
    a.style.display = "none";
    a.download = fileName;
    a.href = blob;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /**
   * Converts an HTMLElement to a Blob via html2canvas.
   * Returns Promise<Blob> so hook consumers (e.g. useFileDownloader) can own
   * the download trigger and status state themselves.
   *
   * Includes the Safari SVG font-text fix (setTimeout tick delay).
   *
   * Accepts typed `transforms` to apply styles in the cloned document.
   *
   * @default format "png"
   * @default quality 1.0 (max)
   * @example
   * ImageHelpers.captureAsBlob(element, {
   *   format: "jpeg",
   *   quality: 0.8,
   *   transforms: [
   *     { selector: ".class-name-1", styles: { padding: "24px" } },
   *     { selector: ".container",  styles: { display: "none" } },
   *     { selector: ".class-name-2",  styles: { display: "block" } },
   *   ],
   * });
   */
  static captureAsBlob = (element: HTMLElement, options: ImageExportOptions = {}): Promise<Blob> => {
    const { format = "png", quality = 1.0, canvasOptions = {}, transforms = [] } = options;
    const scale = canvasOptions.scale ?? 1.1; // default scale to improve text rendering quality, can be overridden by caller

    return new Promise<Blob>((resolve, reject) => {
      // Safari fix: delay by a tick to allow SVG font text to render
      setTimeout(() => {
        html2canvas(element, {
          logging: JSON.parse(process.env.REACT_APP_ENABLE_DEV_DEBUGGING_TOOLS || "false"),
          allowTaint: true,
          useCORS: true,
          ...canvasOptions,
          scale: scale,
          onclone: (doc) => {
            if (!transforms.length) return;
            transforms.forEach(({ selector, styles }) => {
              const el = doc.querySelector<HTMLElement>(selector);
              if (el) Object.assign(el.style, styles);
            });
          },
        })
          .then((canvas) => {
            canvas.toBlob(
              (blob) => {
                if (blob) resolve(blob);
                else reject(new Error("canvas.toBlob returned null"));
              },
              `image/${format}`,
              quality
            );
          })
          .catch(reject);
      }, 0);
    });
  };

  /**
   * Convenience method: capture + download in one call.
   * Accepts typed `transforms` to apply styles in the cloned document.
   *
   * @example
   * ImageHelpers.exportAsImage(element, "image-name.png", {
   *   transforms: [
   *     { selector: ".class-name-1", styles: { padding: "24px" } },
   *     { selector: ".container",  styles: { display: "none" } },
   *     { selector: ".class-name-2",  styles: { display: "block" } },
   *   ],
   * });
   */
  static exportAsImage = async (
    element: HTMLElement,
    fileName: string,
    options: ImageExportOptions = {}
  ): Promise<void> => {
    const { transforms = [], ...exportOptions } = options;
    const width = element.getBoundingClientRect().width;
    const height = element.getBoundingClientRect().height;

    const canvas = await ImageHelpers.captureAsBlob(element, {
      ...exportOptions,
      transforms: transforms,
      canvasOptions: {
        width: width + 48,
        height: height + 48,
        ...exportOptions.canvasOptions,
      },
    });

    const dataUrl = URL.createObjectURL(canvas);
    ImageHelpers.downloadImage(dataUrl, fileName);
    // Revoke the object URL after the browser has had time to initiate the download
    setTimeout(() => URL.revokeObjectURL(dataUrl), 100);
  };

  /**
   * @deprecated Use captureAsBlob instead.
   * Retained for backward compatibility — returns a data URL string.
   */
  static getBlobFromImage = (
    element: HTMLDivElement,
    opts?: Partial<Pick<Html2CanvasOptions, "disableIframe">>
  ): Promise<string> => {
    // Safari fix: delay by a tick to allow SVG font text to render
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        html2canvas(element, {
          logging: process.env.NODE_ENV === "development",
          disableIframe: opts?.disableIframe,
          allowTaint: true,
          useCORS: true,
        }).then((canvas) => {
          resolve(canvas.toDataURL("image/png", 1.0));
        });
      }, 0);
    });
  };
}

export default ImageHelpers;
